'use client';

/**
 * Intro — the arrival sequence. A transparent-dark boot overlay through which
 * the world materializes:
 *
 *   phase 1 (0s)      'STUDYLAB / NODE ONLINE' types in
 *   phase 2 (+0.9s)   'INITIALIZING LABORATORY' + progress shimmer
 *   phase 3 (+1.0s)   wordmark + tagline + ENTER LAB
 *   phase 4 (+1.2s)   overlay fades 0.8s and unmounts
 *
 * Any click or keypress (except Tab/modifiers) enters immediately. Skipped
 * entirely on repeat visits (sessionStorage) and under prefers-reduced-motion.
 * The page content stays in the DOM behind the overlay the whole time.
 */

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useWorld } from '../../lab/store';
import { site } from '../../content/site';
import { getSiteCopy } from '../../content/i18n';
import { useLang } from '../../i18n/client';
import type { Lang } from '../../i18n/config';
import s from './Intro.module.css';

type Stage = 'pending' | 'running' | 'leaving' | 'done';

/** The boot "enter" affordance — a component-local label, not shared chrome. */
const ENTER_LAB: Record<Lang, string> = {
  en: 'Enter Lab',
  ru: 'Войти в лабораторию',
};

const STORAGE_KEY = 'slp-intro-done';
/** The StudyNinja living demo owns its own arrival; the site intro never plays over it. */
const INTRO_FREE_ROUTE = /^\/(en|ru)\/projects\/studyninja\/?$/;
const PHASE2_AT = 900;
const PHASE3_AT = 1900;
const FINISH_AT = 3100;
const FADE_MS = 800;

export default function Intro() {
  const lang = useLang();
  const pathname = usePathname();
  const introFree = INTRO_FREE_ROUTE.test(pathname ?? '');
  const [stage, setStage] = useState<Stage>('pending');
  const [phase, setPhase] = useState(1);
  const finishedRef = useRef(false);
  const timersRef = useRef<number[]>([]);

  /* ── boot decision + timeline ────────────────────────────── */
  useEffect(() => {
    let seen = false;
    try {
      seen = sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      // storage unavailable — treat as first visit
    }
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    if (seen || reducedMotion || introFree) {
      useWorld.getState().setBooted(true);
      setStage('done');
      return;
    }

    setStage('running');
    const timers = timersRef.current;
    timers.push(window.setTimeout(() => setPhase(2), PHASE2_AT));
    timers.push(window.setTimeout(() => setPhase(3), PHASE3_AT));
    timers.push(window.setTimeout(() => finish(), FINISH_AT));

    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    for (const t of timersRef.current) window.clearTimeout(t);
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // ignore — the intro will simply replay next time
    }
    useWorld.getState().setBooted(true);
    setStage('leaving');
    window.setTimeout(() => setStage('done'), FADE_MS + 50);
  };

  /* ── scroll lock while visible ───────────────────────────── */
  useEffect(() => {
    if (stage !== 'running') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [stage]);

  /* ── any keypress skips ahead ────────────────────────────── */
  useEffect(() => {
    if (stage !== 'running') return;
    const onKeyDown = (e: KeyboardEvent) => {
      // Leave Tab and bare modifiers to keyboard navigation.
      if (
        e.key === 'Tab' ||
        e.key === 'Shift' ||
        e.key === 'Control' ||
        e.key === 'Alt' ||
        e.key === 'Meta'
      ) {
        return;
      }
      finish();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  if (stage === 'pending' || stage === 'done') return null;

  return (
    <div
      className={`${s.overlay} ${stage === 'leaving' ? s.leaving : ''}`}
      onPointerDown={() => finish()}
    >
      <div className={s.stack} aria-hidden="true">
        <p className={`${s.lineA} ${phase >= 3 ? s.dimmed : ''}`}>
          <span className={s.typed}>STUDYLAB / NODE ONLINE</span>
        </p>

        <div className={`${s.lineB} ${phase >= 2 ? s.on : ''} ${phase >= 3 ? s.dimmed : ''}`}>
          <p className={s.initText}>INITIALIZING LABORATORY</p>
          <div className={s.progress}>
            <span className={s.progressSheen} />
          </div>
        </div>

        <div className={`${s.hero} ${phase >= 3 ? s.on : ''}`}>
          <p className={s.wordmark}>{site.name}</p>
          <p className={s.tagline}>{getSiteCopy(lang).tagline}</p>
        </div>
      </div>

      <div className={`${s.enterRow} ${phase >= 3 ? s.on : ''}`}>
        {phase >= 3 && (
          <button
            type="button"
            className="btn"
            onClick={(e) => {
              e.stopPropagation();
              finish();
            }}
          >
            {ENTER_LAB[lang]}
          </button>
        )}
      </div>
    </div>
  );
}
